import type {
	ArgsProps,
	NotificationInstance,
} from "antd/es/notification/interface";
import axios from "axios";
import { actions } from "redux-router5";
import { routeNames } from "./common/routes";
import { tokenActions } from "../features/token/tokenSlice";
import { ApiApiFactory } from "./generated-axios-client";
import { createMockAdapter } from "../tests/mockResponses";
import type { Err } from "../types/err";
import { store } from "./store";
import { serviceCountActions } from "../features/service-count/serviceCountSlice";
import { ERRORS_500 } from "../common/constants/constants";

export const getBaseUrl = import.meta.env.VITE_API_BASE_URL;
const instance = axios.create({
	baseURL: import.meta.env.VITE_API_BASE_URL,
	timeout: 100000,
});

if (import.meta.env.VITE_MOCK_TEST === "true") {
	createMockAdapter(instance);
}

export const silentInstance = axios.create({
	baseURL: import.meta.env.VITE_API_BASE_URL,
	timeout: 100000,
});

function errorUnwrap(errorData: any): Err {
	return {
		error: errorData.message,
		status: errorData.status,
		data: errorData?.response?.data?.error,
	};
}

function handleFieldValidationMessage(
	errorFunc: NotificationInstance["error"] | undefined,
	key: string,
	values: Array<any>,
) {
	for (const [_, value] of values.entries()) {
		if (value instanceof Object) {
			for (const [nestKey, nestValue] of Object.entries(value)) {
				errorFunc?.({
					title: `${key}: ${nestKey}`,
					description: `${nestValue}`,
					showProgress: true,
					pauseOnHover: true,
				});
			}
		} else {
			errorFunc?.({
				title: `${key}`,
				description: `${value}`,
				showProgress: true,
				pauseOnHover: true,
			});
		}
	}
}

function isDuplicate(array: Array<ArgsProps>, newObj: ArgsProps) {
	const newObjString = JSON.stringify(newObj);
	return array.some((item) => JSON.stringify(item) === newObjString);
}

// The API raises bare validation codes (e.g. ValidationError("check_out_required")),
// which arrive as field_validation entries and would otherwise be shown verbatim.
// Unmapped codes keep falling through to the raw string.
const VALIDATION_CODE_MESSAGE_KEYS: Record<string, string> = {
	incorrect_date: "checkin_error_incorrect_date",
	incorrect_format: "validation_incorrect_format",
	check_out_required: "validation_check_out_required",
	standby_to_must_be_future: "validation_standby_to_must_be_future",
	standby_from_must_precede_standby_to:
		"validation_standby_from_must_precede_standby_to",
	ticket_must_be_in_review: "validation_ticket_must_be_in_review",
	ticket_approved: "validation_ticket_approved",
	overlapping_session: "validation_overlapping_session",
	overlapping_standby_period: "validation_overlapping_standby_period",
	cannot_reopen_standby_period: "validation_cannot_reopen_standby_period",
};

function translateValidationCode(message: unknown): string {
	if (typeof message !== "string") return `${message}`;
	const messageKey = VALIDATION_CODE_MESSAGE_KEYS[message];
	if (!messageKey) return message;
	return store.getState().intl.datetimeIntl.formatMessage({ id: messageKey });
}

// Permission errors (403) arrive as bare codes in `error.details`
// (e.g. PermissionDenied("not_file_owner")). Map known codes to a friendly
// message; unmapped details fall through unchanged.
const PERMISSION_CODE_MESSAGE_KEYS: Record<string, string> = {
	not_file_owner: "file_delete_not_owner",
	not_time_entry_owner: "time_entry_edit_not_owner",
};

function translatePermissionDetail(detail: unknown): string | undefined {
	if (typeof detail === "string" && PERMISSION_CODE_MESSAGE_KEYS[detail]) {
		return store.getState().intl.datetimeIntl.formatMessage({
			id: PERMISSION_CODE_MESSAGE_KEYS[detail],
		});
	}
	return detail as string | undefined;
}

function debounceWarning(delayInMs: number) {
	const state = store.getState();
	const warningFunc = state.appFeedback.notification?.warning;
	let timeout: number;
	let messages: Array<ArgsProps> = [];
	return (message: ArgsProps) => {
		if (!isDuplicate(messages, message)) {
			messages.push(message);
		}
		clearTimeout(timeout);
		timeout = setTimeout(() => {
			for (const [_, m] of messages.entries()) {
				warningFunc?.(m);
			}
			messages = [];
		}, delayInMs);
	};
}
function debounceError(delayInMs: number) {
	const state = store.getState();
	const errorFunc = state.appFeedback.notification?.error;
	let timeout: number;
	let messages: Array<ArgsProps> = [];
	return (message: ArgsProps) => {
		if (!isDuplicate(messages, message)) {
			messages.push(message);
		}
		clearTimeout(timeout);
		timeout = setTimeout(() => {
			for (const [_, m] of messages.entries()) {
				errorFunc?.(m);
			}
			messages = [];
		}, delayInMs);
	};
}

instance.interceptors.request.use(
	(request) => {
		store.dispatch(serviceCountActions.incrementServiceCounter());
		return request;
	},
	(error) => {
		store.dispatch(serviceCountActions.decrementServiceCounter());
		return error;
	},
);

silentInstance.interceptors.request.use(
	(request) => {
		store.dispatch(serviceCountActions.incrementServiceCounter());
		return request;
	},
	(error) => {
		store.dispatch(serviceCountActions.decrementServiceCounter());
		return error;
	},
);

let debouncedWarning: ((message: ArgsProps) => void) | null = null;
let debouncedError: ((message: ArgsProps) => void) | null = null;

instance.interceptors.response.use(
	(response) => {
		store.dispatch(serviceCountActions.decrementServiceCounter());
		return response;
	},
	(error) => {
		store.dispatch(serviceCountActions.decrementServiceCounter());

		// init debounced funcs
		debouncedWarning ??= debounceWarning(200);
		debouncedError ??= debounceError(200);

		const intl = store.getState().intl.datetimeIntl;
		if (error.response) {
			const errorData = errorUnwrap(error);
			if (errorData.status === 401) {
				store.dispatch(tokenActions.doLogout());
				store.dispatch(actions.navigateTo(routeNames.USER_LOGIN));
			} else if (errorData.status === 403) {
				debouncedError({
					title: translatePermissionDetail(errorData.data?.details),
					showProgress: true,
					pauseOnHover: true,
				});
			} else if (
				errorData.data?.code === "validation_error" &&
				errorData.data.field_validation
			) {
				if (errorData.data.field_validation instanceof Array) {
					for (const [
						_,
						message,
					] of errorData.data.field_validation.entries()) {
						debouncedError({
							title: translateValidationCode(message),
							showProgress: true,
							pauseOnHover: true,
						});
					}
				} else {
					for (const [key, value] of Object.entries(
						errorData.data.field_validation,
					)) {
						handleFieldValidationMessage(debouncedError, key, value);
					}
				}
			} else
				debouncedError({
					title: errorData.status
						? ERRORS_500[errorData.status]
						: intl.formatMessage({ id: "notification_error_server_generic" }),
					showProgress: true,
					pauseOnHover: true,
				});
		} else if (error.code === "ERR_NETWORK") {
			debouncedError({
				title: intl.formatMessage({ id: "notification_error_network_generic" }),
				showProgress: true,
				pauseOnHover: true,
			});
		}
		return error;
	},
);

silentInstance.interceptors.response.use(
	(response) => {
		store.dispatch(serviceCountActions.decrementServiceCounter());
		return response;
	},
	(error) => {
		store.dispatch(serviceCountActions.decrementServiceCounter());
		return error;
	},
);

export const client = ApiApiFactory(
	{
		accessToken: () => store.getState().token.token?.access || "",
		isJsonMime: () => true,
	},
	"",
	instance,
);
export const silentClient = ApiApiFactory(
	{
		accessToken: () => store.getState().token.token?.access || "",
		isJsonMime: () => true,
	},
	"",
	silentInstance,
);
